import { ApiError, api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

type Ping = {
  id: string;
  userId: string;
  message: string;
  attachmentKey: string | null;
  attachmentName: string | null;
  attachmentSize: number | null;
  attachmentType: string | null;
  createdAt: string;
};

type UploadUrlResp = {
  uploadUrl: string;
  key: string;
  pingId: string;
  expiresAt: string;
};

type PingsResp = { pings: Ping[] };

function isAllowedType(t: string): boolean {
  return t.startsWith("image/") || t === "application/pdf";
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function humanAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
}

export function PingPage() {
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const list = useQuery({
    queryKey: ["pings"],
    queryFn: () => api.get<PingsResp>("/pings"),
  });

  const create = useMutation({
    mutationFn: async (payload: { message: string; file: File | null }) => {
      let attachment: {
        pingId: string;
        key: string;
        name: string;
        size: number;
        type: string;
      } | null = null;

      if (payload.file) {
        const f = payload.file;
        const signed = await api.post<UploadUrlResp>("/pings/upload-url", {
          filename: f.name,
          contentType: f.type,
          size: f.size,
        });

        const putRes = await fetch(signed.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": f.type },
          body: f,
        });
        if (!putRes.ok) {
          throw new Error(`R2 upload failed (${putRes.status})`);
        }

        attachment = {
          pingId: signed.pingId,
          key: signed.key,
          name: f.name,
          size: f.size,
          type: f.type,
        };
      }

      const body = attachment
        ? {
            message: payload.message,
            pingId: attachment.pingId,
            attachmentKey: attachment.key,
            attachmentName: attachment.name,
            attachmentSize: attachment.size,
            attachmentType: attachment.type,
          }
        : { message: payload.message };

      return api.post<{ ping: Ping }>("/pings", body);
    },
    onSuccess: () => {
      setMessage("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Ping enviado");
      qc.invalidateQueries({ queryKey: ["pings"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast.error(`Error: ${msg}`);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Escribí un mensaje");
      return;
    }
    if (file) {
      if (file.size > MAX_BYTES) {
        toast.error(`Archivo muy grande (max ${MAX_MB} MB)`);
        return;
      }
      if (!isAllowedType(file.type)) {
        toast.error("Tipo de archivo no permitido (imagen o PDF)");
        return;
      }
    }
    create.mutate({ message: trimmed, file });
  }

  async function openAttachment(id: string) {
    try {
      const { url } = await api.get<{ url: string; expiresAt: string }>(
        `/pings/${id}/attachment-url`,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast.error(`No se pudo abrir: ${msg}`);
    }
  }

  return (
    <>
      <title>Ping · Mihato</title>
      <meta name="robots" content="noindex, nofollow" />
      <main className="mx-auto min-h-screen w-full max-w-2xl bg-neutral-950 px-6 py-12">
        <header className="mb-6">
          <h1 className="font-semibold text-2xl text-neutral-50 tracking-tight">Ping</h1>
          <p className="text-neutral-400 text-sm">
            Feature de prueba de stack. Manda un mensaje, opcionalmente con un archivo.
          </p>
        </header>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border border-neutral-800 bg-neutral-900 p-5"
        >
          <label className="block font-medium text-neutral-200 text-sm" htmlFor="ping-message">
            Mensaje
          </label>
          <textarea
            id="ping-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1000}
            rows={3}
            className="mt-2 block w-full resize-y rounded-xl border border-neutral-700 bg-neutral-950 px-3 py-2 text-base text-neutral-100 placeholder-neutral-500 outline-none focus:border-neutral-500"
            placeholder="Escribí algo…"
          />
          <div className="mt-1 text-neutral-500 text-xs">{message.length}/1000</div>

          <div className="mt-4">
            <label className="block font-medium text-neutral-200 text-sm" htmlFor="ping-file">
              Adjunto (opcional, max {MAX_MB} MB, imagen o PDF)
            </label>
            <input
              ref={fileInputRef}
              id="ping-file"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full text-neutral-300 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-neutral-800 file:px-3 file:py-2 file:text-neutral-200 file:text-sm"
            />
            {file && (
              <div className="mt-1 text-neutral-500 text-xs">
                {file.name} · {humanSize(file.size)}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={create.isPending}
            className="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-neutral-50 px-5 font-medium text-neutral-900 text-sm disabled:opacity-50"
          >
            {create.isPending ? "Enviando…" : "Enviar ping"}
          </button>
        </form>

        <section className="mt-8">
          <h2 className="mb-3 font-medium text-neutral-200 text-sm">
            Mis pings
            {list.data ? ` (${list.data.pings.length})` : ""}
          </h2>

          {list.isPending && <div className="text-neutral-500 text-sm">Cargando…</div>}
          {list.isError && <div className="text-rose-400 text-sm">Error: {list.error.message}</div>}

          <ul className="space-y-2">
            {list.data?.pings.map((p) => (
              <li key={p.id} className="rounded-2xl border border-neutral-800 bg-neutral-900 p-4">
                {p.attachmentKey && p.attachmentName && (
                  <button
                    type="button"
                    onClick={() => openAttachment(p.id)}
                    className="mb-2 inline-flex items-center gap-1 text-neutral-300 text-xs hover:text-neutral-100"
                  >
                    <Paperclip size={14} />
                    {p.attachmentName}
                  </button>
                )}
                <div className="whitespace-pre-wrap break-words text-neutral-100 text-sm">
                  {p.message}
                </div>
                <div className="mt-2 text-neutral-500 text-xs">{humanAgo(p.createdAt)}</div>
              </li>
            ))}
            {list.data?.pings.length === 0 && (
              <li className="text-neutral-500 text-sm">Todavía no enviaste ningún ping.</li>
            )}
          </ul>
        </section>
      </main>
    </>
  );
}
